/**
 * "Assistente, faz isso aqui" — a partir da mensagem que está na tela.
 *
 * O cliente escreve "liga pra mim amanhã depois das 17 horas" e o que o
 * escritório precisa é de um compromisso na agenda. O caminho até aqui era:
 * ler, decorar, trocar de tela, abrir uma conversa com o assistente e escrever
 * de novo o que o cliente disse — de memória, que é onde "depois das 17" vira
 * "às 17". Esta caixa manda o TRECHO LITERAL, com data e hora, e o pedido em
 * cima dele.
 *
 * O que ela NÃO faz: gravar. Quem cria compromisso é o assistente, com as
 * ferramentas do CRM, e cada escrita dele passa pela fila de autorização de
 * sempre — por isso a caixa termina levando a pessoa para a conversa, que é
 * onde ela vê o que ele entendeu e autoriza.
 */
import { useState } from "react";
import { Textarea } from "@/ui/textarea";
import * as api from "../api.js";
import { useAcoes, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { ICONES, Icone, hora } from "../visual.jsx";
import { Botao, Cortina, SeloIA, fecharComPergunta } from "../componentes/comuns.jsx";

/**
 * Os pedidos que o escritório faz todo dia.
 *
 * Não são enfeite: campo em branco é a tela mais difícil do sistema, e quem
 * abre isto pela primeira vez não sabe o que o assistente alcança. Cada um entra
 * no campo como TEXTO — dá para ler, corrigir e mandar, em vez de disparar uma
 * ação escondida que ninguém revisou.
 */
const PRONTOS = [
  {
    chave: "lembrete",
    rotulo: "Criar um lembrete na agenda",
    icone: ICONES.agenda,
    texto: "Crie um compromisso na agenda no dia e na hora que o cliente pediu, com o que combinamos escrito.",
  },
  {
    chave: "tarefa",
    rotulo: "Abrir uma tarefa",
    icone: ICONES.tarefas,
    texto: "Abra uma tarefa para o responsável deste caso com o que o cliente está pedindo, com prazo.",
  },
  {
    chave: "ficha",
    rotulo: "Preencher a ficha",
    icone: ICONES.pessoa,
    texto: "Veja se o cliente informou algum dado da ficha nesta conversa e preencha o que estiver vazio.",
  },
  {
    chave: "resumo",
    rotulo: "Resumir e anotar",
    icone: ICONES.nota,
    texto: "Resuma o que o cliente disse aqui e anote na trilha do caso.",
  },
];

export default function PedirAoAssistente({ mensagens, aoFechar }) {
  const { caso } = useEstado();
  const { avisar } = useAcoes();
  const { ir } = useNavegacao();
  const [pedido, setPedido] = useState("");
  const [enviando, setEnviando] = useState(false);

  const escolhidas = (caso?.mensagens ?? []).filter((m) => mensagens.includes(m.id));

  const mandar = async () => {
    const texto = pedido.trim();
    if (!texto || enviando) return;
    setEnviando(true);
    try {
      const r = await api.pedirAoAssistente({ record_id: caso.id, mensagens, pedido: texto });
      aoFechar();
      /* Leva para a conversa: é lá que ele mostra o que entendeu e pede
         autorização para gravar. Ficar aqui com um "enviado!" esconderia
         justamente a parte em que alguém decide. */
      ir(`/agentes?conversa=${r.conversa}`);
    } catch (e) {
      avisar(e.message);
      setEnviando(false);
    }
  };

  return (
    <Cortina
      aoFechar={aoFechar}
      largura="560px"
      titulo="Pedir ao assistente"
      antesDoTitulo={<SeloIA tamanho={22} fonte={10} />}
      acima="Ele lê o trecho abaixo, usa as ferramentas do CRM neste caso e pede sua autorização antes de gravar qualquer coisa."
      sujo={Boolean(pedido.trim())}
      rodape={
        <div className="flex items-center justify-end gap-2">
          {/* O mesmo fechar do ✕ e do Esc: com pedido escrito, ele pergunta. */}
          <Botao rotulo="Cancelar" aoTocar={fecharComPergunta(aoFechar, Boolean(pedido.trim()))} />
          <Botao
            destaque
            rotulo={enviando ? "Mandando…" : "Pedir"}
            icone={ICONES.assistente}
            aoTocar={mandar}
            titulo={pedido.trim() ? "Manda o pedido e abre a conversa" : "Escreva o que você precisa"}
          />
        </div>
      }
    >
      {/* O TRECHO, como ele vai — e não um "1 mensagem selecionada": o que o
          assistente lê tem de estar à vista de quem pede, senão o pedido é feito
          sobre uma citação que ninguém conferiu. */}
      <div className="mb-3.5 flex flex-col gap-1.5 rounded-xl border border-border bg-secondary/50 p-3">
        {escolhidas.map((m) => (
          <div key={m.id} className="flex gap-2 text-corpo leading-[1.5]">
            <span className="flex-none font-mono text-selo text-muted-foreground">{hora(m.quando)}</span>
            <span className="min-w-0 flex-1">
              <strong className="font-semibold">{m.direcao === "entrada" ? (caso?.nome ?? "Cliente") : "Escritório"}: </strong>
              {m.texto || m.transcricao || (m.arquivo ? `[${m.tipo ?? "arquivo"}]` : "[sem texto]")}
            </span>
          </div>
        ))}
        {!escolhidas.length && <span className="text-corpo text-muted-foreground">A mensagem escolhida não está na tela.</span>}
      </div>

      <div className="mb-2 flex flex-wrap gap-1.5">
        {PRONTOS.map((p) => (
          <button
            key={p.chave}
            type="button"
            onClick={() => setPedido(p.texto)}
            data-dica={p.texto}
            className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card p-[5px_11px] text-apoio font-semibold hover:border-foreground/30"
          >
            <Icone de={p.icone} tamanho={13} largura={1.9} />
            {p.rotulo}
          </button>
        ))}
      </div>

      <Textarea
        value={pedido}
        onChange={(e) => setPedido(e.target.value)}
        onKeyDown={(e) => {
          /* Enter manda, como em toda caixa de escrever da casa. */
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void mandar();
          }
        }}
        autoFocus
        aria-label="O que você quer que o assistente faça"
        placeholder="Ex.: cria um lembrete pra eu ligar pra ela amanhã depois das 17h."
        className="min-h-[92px] resize-y text-corpo leading-[1.6]"
      />
    </Cortina>
  );
}
